import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, ArrowRight, X, Zap } from "lucide-react";
import { TeacherSubject } from "@/hooks/useTeacherSubjects";

interface PrimaryLevelChangeNoticeModalProps {
  open: boolean;
  onClose: () => void;
  primarySubjects: TeacherSubject[];
  onQuickAdd?: () => void;
}

export function PrimaryLevelChangeNoticeModal({
  open,
  onClose,
  primarySubjects,
  onQuickAdd,
}: PrimaryLevelChangeNoticeModalProps) {
  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-xl text-orange-700">
            <AlertTriangle className="w-6 h-6" />
            📢 ประกาศสำคัญ: การเปลี่ยนแปลงระดับชั้นประถม
          </AlertDialogTitle>
          <AlertDialogDescription className="text-left space-y-4 text-base">
            <p className="text-foreground font-medium">เรียน ท่านอาจารย์</p>
            
            <p className="text-foreground">
              ระบบได้มีการปรับปรุงระดับชั้นประถมจากเดิม 1 ระดับ เป็น 2 ระดับ ดังนี้:
            </p>

            <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                <X className="w-4 h-4" />
                <span className="font-medium">ระดับเดิม: "ประถม" (จะถูกลบออก)</span>
              </div>
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <ArrowRight className="w-4 h-4" />
                <span className="font-medium">ระดับใหม่: "ประถมต้น" (ป.1-ป.3)</span>
              </div>
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <ArrowRight className="w-4 h-4" />
                <span className="font-medium">ระดับใหม่: "ประถมปลาย" (ป.4-ป.6)</span>
              </div>
            </div>

            {primarySubjects.length > 0 && (
              <Alert className="bg-yellow-50 dark:bg-yellow-950/20 border-yellow-300 dark:border-yellow-800">
                <AlertDescription>
                  <p className="font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
                    📋 วิชาของท่านที่ต้องดำเนินการ ({primarySubjects.length} วิชา):
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-yellow-800 dark:text-yellow-300">
                    {primarySubjects.map((subject) => (
                      <li key={subject.id}>
                        {subject.subject} - {subject.grade_level} ({subject.course_type})
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="font-semibold text-red-900 dark:text-red-200 mb-2">
                ⚠️ สิ่งที่ท่านต้องดำเนินการ:
              </p>
              <ol className="list-decimal list-inside space-y-1 text-red-800 dark:text-red-300 text-sm">
                <li>ตรวจสอบวิชาที่สอนในระดับประถมของท่าน</li>
                <li>เพิ่มวิชาใหม่ในระดับ "ประถมต้น" และ/หรือ "ประถมปลาย" ตามความเหมาะสม</li>
                <li>วิชาที่มีระดับ "ประถม" เดิมจะถูกลบออกจากระบบในภายหลัง</li>
              </ol>
            </div>

            <p className="text-foreground font-medium text-center">
              กรุณาดำเนินการให้เรียบร้อยก่อนยืนยันข้อมูล
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          {onQuickAdd && (
            <Button 
              onClick={() => {
                onClose();
                onQuickAdd();
              }}
              variant="outline"
              className="w-full sm:w-auto border-orange-600 text-orange-600 hover:bg-orange-50"
            >
              <Zap className="w-4 h-4 mr-2" />
              เพิ่มวิชาอย่างรวดเร็ว
            </Button>
          )}
          <Button onClick={onClose} className="bg-orange-600 hover:bg-orange-700 w-full sm:w-auto">
            รับทราบและดำเนินการเอง
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
